import React, { useState } from 'react';
import './ManageProducts.css';

function ValidationPreviewTable({ validationResults, validationSummary, onUpdateRow, onRemoveRow, userRole, uploadMode = 'add' }) {
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [validatingCell, setValidatingCell] = useState(null);
  
  const handleCellClick = (rowIndex, field, currentValue) => {
    setEditingCell({ rowIndex, field });
    setEditValue(currentValue || '');
  };
  
  const handleCellBlur = async (rowIndex, field) => {
    if (editValue !== validationResults[rowIndex][field]) {
      setValidatingCell({ rowIndex, field });
      await onUpdateRow(rowIndex, field, editValue);
      setValidatingCell(null);
    }
    setEditingCell(null);
  };
  
  const handleKeyDown = (e, rowIndex, field) => {
    if (e.key === 'Enter') {
      handleCellBlur(rowIndex, field);
    } else if (e.key === 'Escape') {
      setEditingCell(null);
      setEditValue('');
    }
  };
  
  const getStatusBadge = (status) => {
    const statusConfig = {
      valid: { className: 'statusValid', label: 'Valid', icon: '✓' },
      warning: { className: 'statusWarning', label: 'Warning', icon: '⚠' },
      error: { className: 'statusError', label: 'Error', icon: '✗' }
    };
    
    const config = statusConfig[status] || statusConfig.error;
    
    return (
      <span className={`validationStatusBadge ${config.className}`}>
        <span className="statusIcon">{config.icon}</span>
        <span className="statusLabel">{config.label}</span>
      </span>
    );
  };
  
  const renderCell = (rowIndex, field, value, hasError) => {
    const isEditing = editingCell?.rowIndex === rowIndex && editingCell?.field === field;
    const isValidating = validatingCell?.rowIndex === rowIndex && validatingCell?.field === field;
    
    // Check if current row has duplicate SKU (same SKU appears multiple times)
    const currentRow = validationResults[rowIndex];
    const currentSKU = currentRow?.sku;
    const hasDuplicateSKU = currentSKU && validationResults.filter(r => r.sku === currentSKU).length > 1;
    
    // Check if this is a variant row (rows after the first with same title+category)
    const variantKey = `${currentRow?.title}_${currentRow?.category_slug || currentRow?.category}`;
    const variantGroup = validationResults.filter(r => 
      `${r.title}_${r.category_slug || r.category}` === variantKey
    );
    const isPartOfVariantGroup = variantGroup.length > 1;
    const variantGroupIndex = isPartOfVariantGroup ? variantGroup.findIndex(r => r === currentRow) : -1;
    const isVariantRow = isPartOfVariantGroup && variantGroupIndex > 0;
    
    // Allow editing for image_urls and description fields even without errors
    // But description is NOT editable for variant rows (inherited from parent)
    const alwaysEditableFields = ['image_urls', 'vendor_remarks', 'has_variants', 'product_variant', 'variant_values'];
    if (!isVariantRow) {
      alwaysEditableFields.push('description');
    }
    const isEditable = hasError || alwaysEditableFields.includes(field) || hasDuplicateSKU;
    
    if (isEditing) {
      // Use textarea for long text fields
      if (['image_urls', 'description', 'vendor_remarks'].includes(field)) {
        return (
          <textarea
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={() => handleCellBlur(rowIndex, field)}
            onKeyDown={(e) => {
              // Allow Enter for line breaks in textarea, Escape to cancel
              if (e.key === 'Escape') {
                setEditingCell(null);
                setEditValue('');
              }
            }}
            className="inlineEditTextarea"
            placeholder={field === 'image_urls' ? 'Separate URLs with | or ,' : `Enter ${field.replace(/_/g, ' ')}`}
            rows={field === 'description' ? 4 : 2}
            autoFocus
          />
        );
      }
      
      return (
        <input
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={() => handleCellBlur(rowIndex, field)}
          onKeyDown={(e) => handleKeyDown(e, rowIndex, field)}
          className="inlineEditInput"
          autoFocus
        />
      );
    }
    
    if (isValidating) {
      return (
        <div className="cellContent validating">
          <span className="validatingSpinner">⏳</span> Validating...
        </div>
      );
    }
    
    // Display value with truncation for long text
    let displayValue = value || '-';
    let fullValue = value;
    
    // Truncate based on field type
    if (value && typeof value === 'string') {
      if (field === 'description' && value.length > 100) {
        displayValue = value.substring(0, 100) + '...';
      } else if (field === 'image_urls' && value.length > 50) {
        displayValue = value.substring(0, 50) + '...';
      } else if (['vendor_remarks', 'hs_code'].includes(field) && value.length > 40) {
        displayValue = value.substring(0, 40) + '...';
      }
    }
    
    return (
      <div
        className={`cellContent ${hasError ? 'hasError' : ''} ${!isEditable ? 'notEditable' : 'editable'}`}
        onClick={() => isEditable && handleCellClick(rowIndex, field, value)}
        title={isEditable ? `Click to edit. ${fullValue && fullValue.length > displayValue.length ? 'Full value: ' + fullValue : ''}` : fullValue}
      >
        {displayValue}
      </div>
    );
  };
  
  if (!validationResults || validationResults.length === 0) {
    return (
      <div className="emptyState">
        <p>No data to preview. Please upload a file.</p>
      </div>
    );
  }
  
  // Detect duplicate SKU + Title combinations (will cause overrides)
  const duplicateProducts = validationResults.reduce((acc, row, idx) => {
    const key = `${row.sku}_${row.title}`;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(idx);
    return acc;
  }, {});
  
  const duplicateProductKeys = Object.keys(duplicateProducts).filter(key => duplicateProducts[key].length > 1);
  const hasDuplicateProducts = duplicateProductKeys.length > 0;

  return (
    <div className="validationPreviewContainer">
      {hasDuplicateProducts && (
        <div className="duplicateWarningBanner" style={{
          background: '#fef3c7',
          border: '2px solid #f59e0b',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '16px',
          color: '#92400e'
        }}>
          <div style={{display: 'flex', alignItems: 'flex-start', gap: '12px'}}>
            <span style={{fontSize: '24px', flexShrink: 0}}>⚠️</span>
            <div>
              <h4 style={{margin: '0 0 8px 0', color: '#92400e', fontSize: '16px', fontWeight: '600'}}>
                Duplicate Products Detected - Import Will Override
              </h4>
              <p style={{margin: '0 0 12px 0', fontSize: '14px', lineHeight: '1.5'}}>
                The following products have identical SKU and Product Name combinations. 
                <strong> Only the last occurrence will be saved - earlier rows will be overridden.</strong>
              </p>
              <div style={{background: 'white', border: '1px solid #fbbf24', borderRadius: '4px', padding: '12px', maxHeight: '200px', overflowY: 'auto'}}>
                {duplicateProductKeys.map((key, idx) => {
                  const [sku, title] = key.split('_');
                  const rowIndices = duplicateProducts[key];
                  return (
                    <div key={idx} style={{marginBottom: idx < duplicateProductKeys.length - 1 ? '8px' : '0', paddingBottom: idx < duplicateProductKeys.length - 1 ? '8px' : '0', borderBottom: idx < duplicateProductKeys.length - 1 ? '1px solid #fef3c7' : 'none'}}>
                      <strong style={{color: '#b45309'}}>SKU: {sku}</strong> | <strong>Title: {title}</strong>
                      <div style={{fontSize: '13px', color: '#78350f', marginTop: '4px'}}>
                        Found in rows: {rowIndices.map(i => `#${i + 1}`).join(', ')}
                        <span style={{marginLeft: '8px', fontWeight: '600', color: '#dc2626'}}>
                          → Row #{rowIndices[rowIndices.length - 1] + 1} will override all others
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
              <p style={{margin: '12px 0 0 0', fontSize: '13px', fontWeight: '600'}}>
                ✏️ Please edit the SKU or Product Name to make them unique before importing.
              </p>
            </div>
          </div>
        </div>
      )}
      
      <div className="tableHeader">
        <h3>Import Preview</h3>
        <div className="validationSummary">
          {/* Use summary counts if available (large batch), otherwise calculate from results */}
          {validationSummary ? (
            <>
              <span className="summaryItem valid">
                ✓ {validationSummary.valid_count.toLocaleString()} Valid
              </span>
              <span className="summaryItem warning">
                ⚠ {validationSummary.warning_count.toLocaleString()} Warnings
                {validationSummary.warning_count > validationResults.filter(r => r.status === 'warning').length && (
                  <span style={{fontSize: '0.85em', marginLeft: '4px', opacity: 0.8}}>
                    ({validationResults.filter(r => r.status === 'warning').length} shown)
                  </span>
                )}
              </span>
              <span className="summaryItem error">
                ✗ {validationSummary.error_count.toLocaleString()} Errors
                {validationSummary.error_count > validationResults.filter(r => r.status === 'error').length && (
                  <span style={{fontSize: '0.85em', marginLeft: '4px', opacity: 0.8}}>
                    ({validationResults.filter(r => r.status === 'error').length} shown)
                  </span>
                )}
              </span>
              <span className="summaryItem" style={{color: '#64748b', fontSize: '0.9em'}}>
                📊 {validationSummary.total_rows.toLocaleString()} Total Rows
              </span>
            </>
          ) : (
            <>
              <span className="summaryItem valid">
                ✓ {validationResults.filter(r => r.status === 'valid').length} Valid
              </span>
              <span className="summaryItem warning">
                ⚠ {validationResults.filter(r => r.status === 'warning').length} Warnings
              </span>
              <span className="summaryItem error">
                ✗ {validationResults.filter(r => r.status === 'error').length} Errors
              </span>
            </>
          )}
          {hasDuplicateProducts && (
            <span className="summaryItem" style={{color: '#f59e0b', fontWeight: '600'}}>
              🔄 {duplicateProductKeys.length} Duplicate Product{duplicateProductKeys.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>
      
      <div className="tableWrapper">
        <table className="validationPreviewTable">
          <thead>
              <tr>
              <th className="stickyColumn">Row #</th>
              <th className="stickyColumn2">Status</th>
              {uploadMode === 'update' && <th>Product ID</th>}
              {uploadMode === 'update' && <th>Variant ID</th>}
              {userRole === 'admin' && <th>Vendor ID</th>}
              {userRole === 'admin' && <th>Vendor Name</th>}
              <th>Title</th>
              <th>Category</th>
              <th>Description</th>
              <th>Has Variants</th>

              {uploadMode !== 'update' && <th>Option 1 Name</th>}
              <th>Option 1 Value</th>
              {uploadMode !== 'update' && <th>Option 2 Name</th>}
              <th>Option 2 Value</th>
              {uploadMode !== 'update' && <th>Option 3 Name</th>}
              <th>Option 3 Value</th>
              <th>SKU</th>
              <th>Price</th>
              <th>Compare Price</th>
              <th>Inventory</th>
              <th>Weight (kg)</th>
              <th>Length (cm)</th>
              <th>Width (cm)</th>
              <th>Height (cm)</th>
              <th>HS Code</th>
              <th>Commission %</th>
              <th>GST Rate (%)</th>
              <th>Image URLs</th>
              <th>Vendor Remarks</th>
              {/* Dynamically render metadata columns */}
              {validationResults.length > 0 && Object.keys(validationResults[0]).filter(key => key.startsWith('meta:')).map(metaKey => (
                <th key={metaKey}>{metaKey.replace('meta:', '').replace(/_/g, ' ')}</th>
              ))}
              <th>Errors/Warnings</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {validationResults.map((row, index) => {
              // Check if this row has duplicate SKU (same SKU appears multiple times)
              const currentSKU = row.sku;
              const duplicateSKUs = currentSKU ? validationResults.filter(r => r.sku === currentSKU) : [];
              const hasDuplicateSKU = duplicateSKUs.length > 1;
              
              // Find parent row (first occurrence of this SKU)
              const parentRowIndex = hasDuplicateSKU ? validationResults.findIndex(r => r.sku === currentSKU) : -1;
              const isVariantOfParent = hasDuplicateSKU && parentRowIndex >= 0 && parentRowIndex < index;
              
              // Check if this row is part of a variant group (same title + category)
              const variantKey = `${row.title}_${row.category_slug || row.category}`;
              const variantGroup = validationResults.filter(r => 
                `${r.title}_${r.category_slug || r.category}` === variantKey
              );
              const isPartOfVariantGroup = variantGroup.length > 1;
              const variantGroupIndex = isPartOfVariantGroup ? variantGroup.findIndex(r => r === row) + 1 : 0;
              const firstVariantIndex = isPartOfVariantGroup ? validationResults.findIndex(r => 
                `${r.title}_${r.category_slug || r.category}` === variantKey
              ) : -1;
              const isFirstInGroup = firstVariantIndex === index;
              
              // Check if this row is a duplicate product (same SKU + Title)
              const productKey = `${row.sku}_${row.title}`;
              const duplicateProductIndices = duplicateProducts[productKey] || [];
              const isDuplicateProduct = duplicateProductIndices.length > 1;
              const isOverriddenRow = isDuplicateProduct && index < duplicateProductIndices[duplicateProductIndices.length - 1];
              const isOverridingRow = isDuplicateProduct && index === duplicateProductIndices[duplicateProductIndices.length - 1];
              
              // Extract metadata fields
              const metaFields = Object.keys(row).filter(key => key.startsWith('meta:'));
              
              return (
              <tr key={index} className={`row-${row.status}`} style={isOverriddenRow ? {background: '#fef3c7'} : {}}>
                <td className="rowNumber stickyColumn">{index + 1}</td>
                <td className="statusCell stickyColumn2">
                  {getStatusBadge(row.status)}
                  {isPartOfVariantGroup && !isFirstInGroup && (
                    <div style={{fontSize: '0.75rem', color: '#64748b', marginTop: '4px', fontStyle: 'italic'}}>
                      Variant {variantGroupIndex} of {variantGroup.length}
                    </div>
                  )}
                  {isPartOfVariantGroup && isFirstInGroup && (
                    <div style={{fontSize: '0.75rem', color: '#3b82f6', marginTop: '4px', fontWeight: '600'}}>
                      📦 {variantGroup.length} Variants
                    </div>
                  )}
                  {isVariantOfParent && !isPartOfVariantGroup && (
                    <div style={{fontSize: '0.75rem', color: '#64748b', marginTop: '4px', fontStyle: 'italic'}}>
                      Variant of Row #{parentRowIndex + 1}
                    </div>
                  )}
                  {isOverriddenRow && (
                    <div style={{fontSize: '0.75rem', color: '#dc2626', marginTop: '4px', fontWeight: '600'}}>
                      ⚠️ Will be overridden
                    </div>
                  )}
                  {isOverridingRow && (
                    <div style={{fontSize: '0.75rem', color: '#f59e0b', marginTop: '4px', fontWeight: '600'}}>
                      🔄 Overrides row{duplicateProductIndices.length > 2 ? 's' : ''} #{duplicateProductIndices.slice(0, -1).map(i => i + 1).join(', ')}
                    </div>
                  )}
                </td>
                {uploadMode === 'update' && (
                  <>
                  <td className="cellEditable" style={{ background: '#f0f9ff', fontFamily: 'monospace', fontSize: '0.85rem' }}>
                    {renderCell(index, 'product_id', row.product_id, row.errors?.product_id)}
                  </td>
                  <td className="cellEditable" style={{ background: '#f7fff0', fontFamily: 'monospace', fontSize: '0.85rem' }}>
                    {renderCell(index, 'variant_id', row.variant_id, row.errors?.variant_id)}
                  </td>
                  </>
                )}
                 {userRole === 'admin' && (
                  <td className="cellEditable">
                    {renderCell(index, 'vendor_id', row.vendor_id, row.errors?.vendor_id)}
                  </td>
                )}
                {userRole === 'admin' && (
                  <td className="cellEditable">
                    {renderCell(index, 'vendor_name', row.vendor_name, row.errors?.vendor_name)}
                  </td>
                )}
                <td className="cellEditable">
                  {renderCell(index, 'title', row.title, row.errors?.title)}
                </td>
                <td className="cellEditable">
                  {renderCell(index, 'category_slug', row.category_slug || row.category, row.errors?.category_slug || row.errors?.category)}
                </td>
                <td className="cellEditable">
                  {renderCell(index, 'description', row.description, row.errors?.description)}
                </td>
      
               
                <td className="cellEditable">
                  {renderCell(index, 'has_variants', row.has_variants, row.errors?.has_variants)}
                </td>
                {uploadMode !== 'update' && (
                  <td className="cellEditable">
                    {renderCell(index, 'Option 1 Name', row['Option 1 Name'], row.errors?.['Option 1 Name'])}
                  </td>
                )}
                <td className="cellEditable">
                  {renderCell(index, 'Option 1 Value', row['Option 1 Value'], row.errors?.['Option 1 Value'])}
                </td>
                {uploadMode !== 'update' && (
                  <td className="cellEditable">
                    {renderCell(index, 'Option 2 Name', row['Option 2 Name'], row.errors?.['Option 2 Name'])}
                  </td>
                )}
                <td className="cellEditable">
                  {renderCell(index, 'Option 2 Value', row['Option 2 Value'], row.errors?.['Option 2 Value'])}
                </td>
                {uploadMode !== 'update' && (
                  <td className="cellEditable">
                    {renderCell(index, 'Option 3 Name', row['Option 3 Name'], row.errors?.['Option 3 Name'])}
                  </td>
                )}
                <td className="cellEditable">
                  {renderCell(index, 'Option 3 Value', row['Option 3 Value'], row.errors?.['Option 3 Value'])}
                </td>
                <td className="cellEditable">
                  {renderCell(index, 'sku', row.sku, row.errors?.sku)}
                </td>
                <td className="cellEditable">
                  {renderCell(index, 'price', row.price, row.errors?.price)}
                </td>
                <td className="cellEditable">
                  {renderCell(index, 'compare_price', row.compare_price || row.compare_at_price, row.errors?.compare_price || row.errors?.compare_at_price)}
                </td>
                <td className="cellEditable">
                  {renderCell(index, 'inventory_quantity', row.inventory_quantity || row.inventory, row.errors?.inventory_quantity || row.errors?.inventory)}
                </td>
                <td className="cellEditable">
                  {renderCell(index, 'weight_kg', row.weight_kg, row.errors?.weight_kg)}
                </td>
                <td className="cellEditable">
                  {renderCell(index, 'length_cm', row.length_cm, row.errors?.length_cm)}
                </td>
                <td className="cellEditable">
                  {renderCell(index, 'width_cm', row.width_cm, row.errors?.width_cm)}
                </td>
                <td className="cellEditable">
                  {renderCell(index, 'height_cm', row.height_cm, row.errors?.height_cm)}
                </td>
                <td className="cellEditable">
                  {renderCell(index, 'hs_code', row.hs_code, row.errors?.hs_code)}
                </td>
                <td className="cellEditable">
                  {/* Use brand_commission for editing (CSV field), commission_pct as fallback (API field) */}
                  {renderCell(index, 'brand_commission', row.brand_commission || row.commission_pct, row.errors?.brand_commission || row.errors?.commission_pct)}
                </td>
                <td className="cellEditable">
                  {renderCell(index, 'gst_rate', row.gst_rate, row.errors?.gst_rate)}
                </td>
                <td className="cellEditable imageUrlsCell">
                  {renderCell(index, 'image_urls', row.image_urls, row.errors?.image_urls)}
                </td>
                <td className="cellEditable">
                  {renderCell(index, 'vendor_remarks', row.vendor_remarks, row.errors?.vendor_remarks)}
                </td>
                {/* Render metadata cells */}
                {metaFields.map(metaKey => (
                  <td key={metaKey} className="cellEditable">
                    {renderCell(index, metaKey, row[metaKey], row.errors?.[metaKey])}
                  </td>
                ))}
                <td className="errorCell">
                  {/* Display errors */}
                  {row.errors && Object.keys(row.errors).length > 0 && (
                    <div className="errorList">
                      {Object.entries(row.errors).map(([field, message]) => (
                        <div key={field} className="errorItem errorType">
                          <strong>{field}:</strong> {message}
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Display warnings */}
                  {row.warnings && row.warnings.length > 0 && (
                    <div className="warningList">
                      {row.warnings.map((warning, wIdx) => (
                        <div key={wIdx} className="warningItem">
                          ⚠ {warning}
                        </div>
                      ))}
                    </div>
                  )}
                </td>
                <td className="actionsCell">
                  <button
                    onClick={() => onRemoveRow(index)}
                    className="removeRowButton"
                    title="Remove this row"
                  >
                    ✗
                  </button>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      
      <div className="tableFooter">
        <p className="footerNote">
          <strong>Note:</strong> Click on cells with errors to edit them inline. 
          Description fields, Image URLs, and Vendor Remarks are always editable.
          Separate multiple image URLs with | or comma. Only rows with "Valid" or "Warning" status will be imported.
          {hasDuplicateProducts && (
            <>
              <br/><strong style={{color: '#dc2626'}}>⚠️ Warning:</strong> Rows with identical SKU and Product Name will override each other. 
              Please edit to make them unique.
            </>
          )}
        </p>
      </div>
    </div>
  );
}

export default ValidationPreviewTable;